import React, { useEffect, useRef } from 'react';
import { connect, useSelector } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleModal } from 'apps/auth/actions/appAction';
import { updateExtraByName } from 'apps/auth/actions/extraAction';
import { changeFormFieldValue, updateFieldValues, updateFormProperties } from 'apps/auth/actions/formAction';
import { Badge } from 'apps/auth/components/common/Badge/Badge';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper, PanelContent, PanelFrame, PanelRightBlock } from 'apps/auth/components/common/Panel/styles';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import { getRecipientTypeParam, transactionMethods } from 'apps/auth/components/utils/AmplitudeUtils';
import { METHOD_UNAVAILABLE_SHOWN, SENDING_METHOD_SELECTED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import {
    PAYMENT_COUNTRY,
    PAYMENT_CURRENCY,
    PAYMENT_FLOW,
    PAYMENT_METHOD_AMPLITUDE,
    PAYMENT_METHOD_CATEGORY,
    PAYMENT_METHOD_TYPE,
    PAYOUT_COUNTRY,
    PAYOUT_CURRENCY,
    PAYOUT_METHOD,
    PAYOUT_METHOD_CATEGORY,
    RECIPIENT_TYPE,
} from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { PAYMENT } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { PAYMENT_GLOBAL_ACCOUNT } from 'apps/auth/constants/Constants';
import { COMMISSION, RECALCULATE } from 'apps/auth/constants/ExtraNames';
import { AMOUNT_FROM, DELIVERY_METHOD } from 'apps/auth/constants/fieldsName';
import { getCurrencyIcon, onIconRenderError, toLowerCase } from 'apps/auth/helpers/utils';
import { GTPaymentMethodService } from 'apps/auth/services/GTPaymentMethodService';

const DcGaAccount = ({ badge = '', actionBanner = '', ...props }) => {
    const popupRef = useRef(null);
    const paymentMethodService = useRef(null);
    const deliveryMethod = useSelector(({ form }) => form[props.formName]?.fields[DELIVERY_METHOD]?.value);
    const { toCountry, fromCountry, toCurrency, fromCurrency, userCountry, payoutCategory, paymentCategory } = useSelector(({ extra }) => extra);

    const amplitudeSendingMethod = transactionMethods[props.paymentMethod?.method];
    const amplitudeDeliveryMethod = transactionMethods[deliveryMethod];

    const { changeAmount, continueTransferFlow, changePaymentOption } = useSelector(({ messages: { pageMessages } }) => pageMessages);

    useEffect(() => {
        paymentMethodService.current = new GTPaymentMethodService(
            props.updateFieldValues,
            props.updateFormProperties,
            props.formName,
            toCountry?.code,
            toCurrency?.code,
            deliveryMethod,
        );
    }, []);

    const isNotEnoughFunds = props.isNotEnoughFunds;
    const isBelowMinAmount = props.isBelowMinAmount;
    const isDisabledAccount = isNotEnoughFunds || isBelowMinAmount;

    const updateAmount = (value) => {
        props.updateExtraByName(COMMISSION, props.paymentMethod?.commission).then(() => {
            props.updateExtraByName(RECALCULATE, true);
            props.changeFormFieldValue(props.formName, value, AMOUNT_FROM).then(() => {
                paymentMethodService.current.changePaymentMethod(props.method, props.code, () => {
                    props.returnToCalculator();
                });
            });
        });
    };

    const onClickPopupButton = (e) => {
        e.preventDefault();
        if ((isBelowMinAmount || isNotEnoughFunds) && props.returnToCalculator) {
            updateAmount(props.balance);
        }
        popupRef.current?.close();
    };

    const getPopup = () => {
        let icon, buttonText;
        icon = isBelowMinAmount || isNotEnoughFunds ? 'arrow-back' : 'retry';
        buttonText = changeAmount;
        return (
            <SimplePopup text={props.popupText} popupRef={popupRef}>
                <PopupButton iconName={icon} text={buttonText} onClick={onClickPopupButton} />
            </SimplePopup>
        );
    };

    const onClickHandler = (e) => {
        if (props.isTemporarilyUnavailableSource) {
            amplitude.getInstance().logEvent(METHOD_UNAVAILABLE_SHOWN, {
                [PAYMENT_COUNTRY]: userCountry?.nameEn,
                [PAYOUT_COUNTRY]: toCountry?.nameEn,
                [PAYMENT_CURRENCY]: fromCurrency?.code,
                [PAYOUT_CURRENCY]: toCurrency?.code,
                [PAYMENT_METHOD_TYPE]: PAYMENT,
            });
            popupRef.current?.open(
                <SimplePopup text={props.temporarilyUnavailableReason} popupRef={popupRef}>
                    <PopupButton
                        iconName="arrow-forward"
                        text={changePaymentOption}
                        onClick={() => {
                            popupRef.current?.close();
                        }}
                    />
                </SimplePopup>,
            );
        } else if (isDisabledAccount) {
            e.preventDefault();
            popupRef.current?.open(getPopup());
        } else if (actionBanner) {
            e.preventDefault();
            popupRef.current?.open(
                <SimplePopup text={<DangerouslyHtml dangerouslyHtml={actionBanner} />} popupRef={popupRef}>
                    <PopupButton
                        iconName="arrow-forward"
                        text={continueTransferFlow}
                        onClick={() => {
                            props.clickHandler(e, PAYMENT_GLOBAL_ACCOUNT, props.account.code);
                            popupRef.current?.close();
                        }}
                    />
                    <PopupButton
                        iconName="retry"
                        text={changePaymentOption}
                        onClick={() => {
                            popupRef.current?.close();
                        }}
                    />
                </SimplePopup>,
            );
        } else if (props.clickHandler) {
            props.clickHandler(e, PAYMENT_GLOBAL_ACCOUNT, props.account.code);
            amplitude.getInstance().logEvent(SENDING_METHOD_SELECTED, {
                [PAYOUT_COUNTRY]: toCountry?.nameEn,
                [PAYMENT_COUNTRY]: fromCountry?.nameEn,
                [PAYOUT_METHOD]: amplitudeDeliveryMethod,
                [PAYMENT_METHOD_AMPLITUDE]: amplitudeSendingMethod,
                [PAYOUT_METHOD_CATEGORY]: payoutCategory,
                [PAYMENT_METHOD_CATEGORY]: paymentCategory,
                [PAYMENT_FLOW]: props.getPaymentFlow?.(),
                [RECIPIENT_TYPE]: getRecipientTypeParam(props.recipientType),
                [PAYOUT_CURRENCY]: toCurrency?.code,
                [PAYMENT_CURRENCY]: fromCurrency?.code,
            });
        }
    };

    const getIconOrBadge = (isNoFunds, isMinAmount) => {
        let firstComponent = null,
            secondComponent = null;
        if (isNoFunds || isMinAmount) {
            firstComponent = <Icon name={'alert'} size={'16px'} color={'lightGray'} />;
        } else if (badge) {
            firstComponent = <Badge variant="primary" caption={badge} />;
        }
        return [firstComponent, secondComponent].map((el, index) => el && <React.Fragment key={index}>{el}</React.Fragment>);
    };

    const alias = `${props.account.balance}  ${props.account.currencyCode}`;
    return (
        <>
            <Panel onClick={onClickHandler} negativeMargin isHoverable>
                <PanelFrame>
                    <DiskBadgeWrapper>
                        <DiskBadge
                            isDisabled={isDisabledAccount || props.isTemporarilyUnavailableSource}
                            src={getCurrencyIcon(toLowerCase(props.account.currencyCode))}
                            alt={toLowerCase(props.account.currencyCode)}
                            onError={onIconRenderError}
                        />
                    </DiskBadgeWrapper>
                    <PanelContent>
                        <Text color={isDisabledAccount || props.isTemporarilyUnavailableSource ? 'secondary' : 'primary'}>{alias}</Text>
                    </PanelContent>
                    <PanelRightBlock flex>{getIconOrBadge(isNotEnoughFunds, isBelowMinAmount)}</PanelRightBlock>
                </PanelFrame>
            </Panel>
            <PopupNew ref={popupRef} />
        </>
    );
};

export default connect(null, {
    changeFormFieldValue,
    toggleModal,
    updateExtraByName,
    updateFieldValues,
    updateFormProperties,
})(DcGaAccount);
